import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import {
  ApolloError,
  MutationHookOptions,
  MutationTuple,
} from '@apollo/client';

import { alertApiRef, useApi } from '@backstage/core-plugin-api';

import { GraphQLAppError } from './GraphQLAppError';
import { GraphQLResult, getError, getTypeOrNull, isError } from './apollo';
import {
  ListUpdateMutationCacheConfig,
  resolveUpdateMutationOptions,
} from './apollo/listCache';
import { type Error as AppError, Exact, InputMaybe } from './graphql';
import { FieldMask, fieldMask } from './utils';

// Options that can be passed when using the hook
export type StandardUpdateHookOptions<TResult> = {
  onComplete?: (result: TResult) => void;
  onError?: 'post-alert' | ((error: Error | ApolloError) => void);
};

// The result type returned by the hook
export type StandardUpdateHookResult<
  TResult,
  TPath extends string,
  TInput = unknown,
> = {
  error?: Error | ApolloError;
  isUpdating: boolean;
  called: boolean;
  result?: TResult;
} & {
  [K in TPath]: (
    input: TInput,
    updateMask?: FieldMask<TInput>,
  ) => Promise<TResult>;
};

export function withStandardUpdateHook<
  // TData is the full mutation result type
  TData extends {
    [K in TPath]?:
      | TResult
      | { __typename: 'Error'; message?: string | null }
      | null;
  },
  // TResult is the expected result type when successful
  TResult extends GraphQLResult | null,
  // TPath is the key in the mutation result where the data lives
  TPath extends string,
  // TInput is the input type for the update mutation
  TInput = unknown,
  TListItem extends Record<string, unknown> = Record<string, unknown>,
  TListScope = unknown,
>(
  useMutation: (
    options?: MutationHookOptions<
      TData,
      Exact<{ input: TInput; updateMask?: InputMaybe<string> }>
    >,
  ) => MutationTuple<
    TData,
    Exact<{ input: TInput; updateMask?: InputMaybe<string> }>
  >,
  dataPath: TPath,
  // Mutation options are optional and can be used to customize the mutation
  mutationOptions?: Pick<
    MutationHookOptions<
      TData,
      Exact<{ input: TInput; updateMask?: InputMaybe<string> }>
    >,
    'awaitRefetchQueries' | 'fetchPolicy' | 'refetchQueries' | 'update'
  > & {
    cacheUpdate?: ListUpdateMutationCacheConfig<TListItem, TListScope, TData>;
  },
) {
  const resolvedMutationOptions = resolveUpdateMutationOptions<
    TData,
    Exact<{ input: TInput; updateMask?: InputMaybe<string> }>,
    TListItem,
    TListScope
  >(mutationOptions);

  return (
    options: StandardUpdateHookOptions<TResult> = {},
  ): StandardUpdateHookResult<TResult, TPath, TInput> => {
    const alert = useApi(alertApiRef);
    const [
      mutate,
      { loading: isUpdating, error: mutationError, data, called },
    ] = useMutation(resolvedMutationOptions);

    const update = useCallback(
      async (
        input: TInput,
        updateMask?: FieldMask<TInput>,
      ): Promise<TResult> => {
        const response = await mutate({
          variables: {
            input,
            updateMask: updateMask ? fieldMask(updateMask) : undefined,
          } as Exact<{ input: TInput; updateMask?: InputMaybe<string> }>,
        });
        const result = response.data?.[dataPath];

        if (isError(result)) {
          const appError = result as AppError;
          throw new GraphQLAppError(
            appError.message ?? 'Unknown error',
            appError.code,
          );
        }

        const typedResult = result as TResult;
        return typedResult;
      },
      [mutate],
    );

    const result = data?.[dataPath];
    const typedResult = getTypeOrNull(result) as TResult | undefined;

    const error = useMemo(() => {
      if (mutationError) return mutationError;
      const appError = getError(result);
      if (appError) return appError;
      return undefined;
    }, [mutationError, result]);

    // we use a ref to avoid that the onComplete is called on every render
    // if its not stable.
    const onCompleteValue = useRef(options.onComplete);
    useLayoutEffect(() => {
      onCompleteValue.current = options.onComplete;
    }, [options.onComplete]);

    useEffect(() => {
      if (typedResult && onCompleteValue.current) {
        onCompleteValue.current(typedResult);
      }
    }, [typedResult]);

    // we use a ref to avoid that the onError is called on every render
    // if its not stable.
    const onErrorValue = useRef(options.onError);
    useLayoutEffect(() => {
      onErrorValue.current = options.onError;
    }, [options.onError]);

    useEffect(() => {
      if (error) {
        if (onErrorValue.current === 'post-alert') {
          alert.post({
            severity: 'error',
            message: error.message,
          });
        } else {
          onErrorValue.current?.(error);
        }
      }
    }, [error, alert, onErrorValue]);

    return {
      [dataPath]: update,
      error,
      isUpdating,
      called,
      result: error ? undefined : (result as TResult),
    } as StandardUpdateHookResult<TResult, TPath, TInput>;
  };
}
